import { useEffect, useMemo, useReducer, useRef, useState } from 'react';

import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Heading,
  Icon,
  Modal,
  Paragraph,
  Spinner,
  TextButton,
} from '@zillow/constellation-10';
import { IconHouseFilled } from '@zillow/constellation-icons';

import { GTM_EVENTS } from '@zg-rentals/google-tag-manager';
import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { getListingAliasFromPath } from '@zg-rentals/string-utils';
import { RentalManagerUserTrackingEvent } from '@zg-rentals/ts-rental-manager-api-beta-client';
import {
  useClientAppConfigWebHandler_getFeature,
  useRentalManagerUserPropertyDetailsWebApiHandler_details,
  useRentalManagerUserTrackingWebApiHandler_event,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import accessUtils from '../../../../srcLegacy/shared/utils/accessUtils';
import contextUtils from '../../../../srcLegacy/shared/utils/contextUtils';
import propertyUtils from '../../../../srcLegacy/shared/utils/propertyUtils';
import typeCheckUtils from '../../../../srcLegacy/shared/utils/typeCheckUtils';
import { useContracts, useCreateProperty, useValidateListing } from '../../../hooks';
import { setIsNewListing } from '../../../utils/navigationCacheUtils';
import { useSavePropertyInfo } from './hooks';
import { listingErrors as listingErrorsReducer, listing as listingReducer } from './reducers';
import { shouldDisplayRoomForRentCheckbox } from './utils';

import PropertyConstants from '../../../constants/PropertyConstants';
import { EXTERNAL_ROUTES } from '../../../constants/urls';
import {
  BEHAVIOR_OPTIONS,
  EMPTY_ERROR,
  EMPTY_LISTING,
  MAX_NUM_MULTIPLEX_UNITS_ERROR,
  MAX_NUM_MULTIPLEX_UNITS_VALUE,
  MAX_NUM_UNITS_ERROR,
  MAX_NUM_UNITS_VALUE,
  NUMBER_OF_UNITS_ERROR,
  POSITIVE_NUMBER_OF_UNITS_ERROR,
  STEPS,
} from './constants';

import {
  EVENT_IDS as ADD_PROPERTY_EVENT_IDS,
  emitConfirmCreateListingEvent,
  emitConfirmSeeAllActionsEvent,
  emitCreateListingEvent,
  emitGoBackAndEditAddressEvent,
  emitSeeAllActionsEvent,
} from '../../../analytics/events/addPropertyModal';
import AddPropertyForm from './components/AddPropertyForm';
import ConfirmAddress from './components/ConfirmAddress';
import DeleteButton from './components/DeleteButton';
import MultiplexUnitEntry from './components/MultiplexUnitEntry';
import MultiplexUnitsAdded from './components/MultiplexUnitsAdded';
import SelectAvailableActions from './components/SelectAvailableActions';

import { trackGtmEvent } from '../../../analytics';
import {
  emitMultiplexAddListingDetailsClickEvent,
  emitMultiplexConfirmAddressClickEvent,
  emitMultiplexConfirmUnitEntryClickEvent,
  EVENT_IDS as MULTIPLEX_EVENT_IDS,
} from '../../../analytics/events/multiplex';
import { useAuth } from '../../AuthProvider';
import ListingActivationTerms from '../../ListingActivationTerms';

import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';
import type {
  RentalManagerAuthCheckUserInfoWebApiDto,
  UserPropertyMultiplexCreateUnitResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { ListingReducerProps } from './reducers';
import type { UserProps } from '../../../../srcLegacy/shared/model/User';
import type { MultiplexUnitEntryRef } from './components/MultiplexUnitEntry';
import type { MultiplexUnitsAddedRef } from './components/MultiplexUnitsAdded';

const {
  LISTING_CODES_BY_TYPE,
  PROPERTY_TYPES,
  PROPERTY_TYPES: { COMMUNITY, MULTIPLEX },
  PROPERTY_TYPES_BY_CODE,
  LISTING_TYPES: { RENTAL, ROOM },
  ERROR_CODE: { IS_MULTI_FAMILY_RESTRICTED },
} = PropertyConstants;

type Props = {
  isOpen?: boolean;
  onClose?: () => void;
  behavior?: (typeof BEHAVIOR_OPTIONS)[keyof typeof BEHAVIOR_OPTIONS];
  user?: RentalManagerAuthCheckUserInfoWebApiDto | UserProps;
};

const AddPropertyModal = ({
  isOpen = false,
  onClose = () => {},
  behavior = BEHAVIOR_OPTIONS.ADD,
  user: userProp,
}: Props): JSX.Element => {
  const authContext = useAuth?.();
  const user = userProp ?? authContext?.user;

  const multiplexUnitEntryRef = useRef<MultiplexUnitEntryRef>(null);
  const multiplexUnitsAddedRef = useRef<MultiplexUnitsAddedRef>(null);
  const [isMultiplexProcessing, setIsMultiplexProcessing] = useState(false);

  const shouldRedirectToPayments =
    typeof window !== 'undefined' ? contextUtils.isRedirectToPayments(window.location.search) : false;

  const queryClient = useQueryClient();
  const { createProperty: createPropertyMutation } = useCreateProperty();
  const { savePropertyInfo } = useSavePropertyInfo();
  const { mutate: trackEvent } = useRentalManagerUserTrackingWebApiHandler_event();

  const [step, setStep] = useState<(typeof STEPS)[keyof typeof STEPS]>(STEPS.INPUT_ADDRESS);
  const [isSelectActionFinalStep, setIsSelectActionFinalStep] = useState(false);
  const [unitNumberDetected, setUnitNumberDetected] = useState(false);
  const [isValidatedWithoutUnitDetected, setIsValidatedWithoutUnitDetected] = useState(false);
  const [propertyAlreadyPostedWarningMessage, setPropertyAlreadyPostedWarningMessage] = useState<
    string | Array<JSX.Element | string>
  >('');
  const [isRefocusingAddressInput, setIsRefocusingAddressInput] = useState(false);
  const [unitsCreated, setUnitsCreated] = useState<Array<UserPropertyMultiplexCreateUnitResponseWebDto>>([]);
  const [hasPartialUnitsSucceeded, setHasPartialUnitsSucceeded] = useState<boolean>(false);

  // Get listingAlias from path for edit mode
  const listingAliasFromQuery = typeof window !== 'undefined' ? getListingAliasFromPath(window.location.pathname) : '';

  const { data: propertyDetailsData } = useRentalManagerUserPropertyDetailsWebApiHandler_details(
    {
      listingAlias: listingAliasFromQuery,
    },
    {
      enabled: Boolean(listingAliasFromQuery) && behavior === BEHAVIOR_OPTIONS.EDIT,
    },
  );

  const getInitialListing = () => {
    // TODO: After migration is complete, use Nextjs router.query
    const urlQueryParams = new URLSearchParams(window.location.search);
    const urlContainsAddressParam = urlQueryParams.get('address') !== null;

    return !urlContainsAddressParam
      ? EMPTY_LISTING
      : {
          ...EMPTY_LISTING,
          address: urlQueryParams.get('address')?.replace(/-/g, ' '),
          unit: urlQueryParams.get('unit') ?? undefined,
        };
  };

  const initialListing = getInitialListing();

  const [listing, setListing] = useReducer(listingReducer, initialListing);
  const [errorObj, setErrorObj] = useReducer(listingErrorsReducer, EMPTY_ERROR);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [isPrimaryButtonLoading, setIsPrimaryButtonLoading] = useState(false);
  const [isSecondaryButtonLoading, setIsSecondaryButtonLoading] = useState(false);
  const [isImpreciseLocation, setIsImpreciseLocation] = useState(false);

  const { data: canAccessMultiplexListingFlowResponse = 'false' } = useClientAppConfigWebHandler_getFeature({
    appName: 'zrm_web',
    featureName: 'canAccessMultiplexListingFlow',
  });

  const isMultiplexType = listing?.propertyType === MULTIPLEX;
  const isPromptUnitNumberCheckRequired = isMultiplexType ? false : !unitNumberDetected;

  const contracts = useContracts(setListing, setErrorObj, setIsSubmitDisabled, user ?? undefined);

  const { verifyCanSubmitForm, handleCheckLocationRestriction } = useValidateListing(
    listing,
    setListing,
    errorObj,
    setErrorObj,
    setUnitNumberDetected,
    setIsValidatedWithoutUnitDetected,
    setPropertyAlreadyPostedWarningMessage,
    setIsImpreciseLocation,
    user ?? undefined,
    contracts,
  );

  const canAccessMultiplexListingFlow = accessUtils.canAccessMultiplexListingFlow({
    canAccessMultiplexListingFlowResponse,
  });

  useEffect(() => {
    if (isOpen) {
      trackEvent({
        getParams: {
          trackingEvent: RentalManagerUserTrackingEvent.OPEN_ADD_PROPERTY_MODAL,
        },
      });
    } else {
      setIsMultiplexProcessing(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (listingAliasFromQuery && propertyDetailsData && behavior === BEHAVIOR_OPTIONS.EDIT) {
      setListing({
        type: 'UPDATE_ALL',
        payload: {
          address: propertyUtils.formatAddress({
            street: propertyDetailsData.street,
            city: propertyDetailsData.city,
            state: propertyDetailsData.state,
            zip: propertyDetailsData.zip,
            unit: propertyDetailsData.unit,
          }).formattedAddressWithoutUnit,
          isRoomForRent: propertyDetailsData.listingType.code === Number(LISTING_CODES_BY_TYPE[ROOM]),
          isMultifamily: propertyDetailsData.isMultifamily,
          numUnits: propertyDetailsData.numUnits != null ? String(propertyDetailsData.numUnits) : undefined,
          customerId: propertyDetailsData.customerId,
          propertyType: propertyDetailsData.propertyType
            ? PROPERTY_TYPES_BY_CODE[propertyDetailsData.propertyType.code]
            : undefined,
          unit: propertyDetailsData.unit != null ? String(propertyDetailsData.unit) : undefined,
          listingAlias: listingAliasFromQuery,
          isAddressEditable: propertyDetailsData.isAddressEditable,
          duplicateAccountListingAlias: null,
        },
      });
    }
  }, [behavior, propertyDetailsData, isOpen, listingAliasFromQuery]);

  const handleListingChange = (keyName: keyof ListingReducerProps, newValue: string | boolean) => {
    const newListing = {
      ...listing,
      [keyName]: newValue,
    };

    setErrorObj({ type: keyName, payload: '' });
    // if the unit was changed, the entire address will be revalidated
    if (keyName === 'unit') {
      setErrorObj({ type: 'address', payload: '' });
    }

    const isMultiplexType = newListing.propertyType === MULTIPLEX;

    if (newListing.numUnits) {
      const numUnitsValue = String(newListing.numUnits);
      const isNumericValue = typeCheckUtils.isNumeric(numUnitsValue);
      const hasDecimal = numUnitsValue.includes('.');

      if (!isNumericValue || hasDecimal) {
        setErrorObj({ type: 'numUnits', payload: NUMBER_OF_UNITS_ERROR });
      } else {
        const numUnits = Number(numUnitsValue);

        if (numUnits <= 0) {
          setErrorObj({ type: 'numUnits', payload: POSITIVE_NUMBER_OF_UNITS_ERROR });
        } else if (isMultiplexType && numUnits > MAX_NUM_MULTIPLEX_UNITS_VALUE) {
          setErrorObj({ type: 'numUnits', payload: MAX_NUM_MULTIPLEX_UNITS_ERROR });
        } else if (!isMultiplexType && numUnits > MAX_NUM_UNITS_VALUE) {
          // community / other numUnits types
          setErrorObj({ type: 'numUnits', payload: MAX_NUM_UNITS_ERROR });
        }
      }
    }

    setListing({
      type: keyName,
      payload: keyName === 'numUnits' && typeof newValue === 'string' ? newValue.trim() : newValue,
    });

    if (keyName === 'propertyType') {
      setListing({ type: 'isMultifamily', payload: newValue === COMMUNITY });
    }
  };

  const onChangePropertyType = (newPropertyType: string) => handleListingChange('propertyType', newPropertyType);

  const verifyInputAndGoToConfirmSteps = (csEvent: () => void, onValidationSuccess?: () => void) => {
    setIsSubmitDisabled(true);

    verifyCanSubmitForm({ isPromptUnitNumberCheckRequired })
      .then((isValid) => {
        if (isValid) {
          // run any cleanup logic before changing step
          onValidationSuccess?.();
          setStep(STEPS.CONFIRM_ADDRESS);
          csEvent();
        }
      })
      .finally(() => {
        setIsSubmitDisabled(false);
        setIsPrimaryButtonLoading(false);
        setIsSecondaryButtonLoading(false);
      });
  };

  const createProperty = async () => {
    const { isRoomForRent, propertyType, address, unit, numUnits, customerId, duplicateAccountListingAlias } = listing;
    const listingType = isRoomForRent ? ROOM : RENTAL;
    const isCommunityType = propertyType === COMMUNITY;

    // Duplicate listing exists. Redirect to existing listing rather than creating new one
    if (duplicateAccountListingAlias) {
      setListing({
        type: 'listingAlias',
        payload: duplicateAccountListingAlias,
      });

      const path = isSelectActionFinalStep
        ? null
        : `/rental-manager/properties/${duplicateAccountListingAlias}/listing`;
      return { path };
    }

    if (!address || !propertyType) {
      return { path: null };
    }

    const responseData = await createPropertyMutation({
      listingType,
      propertyType,
      fullAddress: address,
      ...(isCommunityType ? { numUnits } : { unitNumber: unit }),
      customerId: listingType === ROOM ? null : customerId,
    });

    const { listingAlias } = responseData;

    // feature access
    const canAccessModularListing = accessUtils.canAccessModularListing({
      currentProperty: {
        isMultifamily: responseData.isMultifamily,
        listingTypeCode: responseData.listingType.code,
        dateLastActivatedTimestamp: responseData.lastActivatedMs?.toString() ?? '',
      },
      user: {
        features: {
          canAccessModularListing: user?.features?.canAccessModularListing ?? false,
        },
      },
    });

    // analytics
    const clickstreamEvent =
      canAccessModularListing && isSelectActionFinalStep
        ? emitConfirmSeeAllActionsEvent
        : emitConfirmCreateListingEvent;

    if (user) {
      clickstreamEvent({ currentProperty: responseData, user });
    }
    trackGtmEvent({ event: GTM_EVENTS.RENTAL_MANAGER_ADD_PROPERTY });

    setListing({
      type: 'listingAlias',
      payload: listingAlias,
    });

    queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });

    if (isSelectActionFinalStep) {
      // if the user hit "See All Available Actions" earlier,
      // they go to the SELECT_ACTIONS step in the wizard instead of transitioning to a new URL
      return { path: null };
    }
    if (canAccessModularListing) {
      setIsNewListing(true);
      return {
        path: `/rental-manager/properties/${listingAlias}/create-listing`,
        additionalData: responseData,
      };
    }
    return {
      path: isSelectActionFinalStep ? null : `/rental-manager/properties/${listingAlias}/listing`,
      additionalData: responseData,
    };
  };

  const handleErrorReset = () => {
    setIsSubmitDisabled(false);

    // take user back to add property form to see the error.
    setStep(STEPS.INPUT_ADDRESS);
    setIsPrimaryButtonLoading(false);
    setIsSecondaryButtonLoading(false);
  };

  const editProperty = async () => {
    try {
      await savePropertyInfo(
        {
          fullAddress: listing.address || '',
          unit: !listing.isMultifamily ? listing.unit : undefined,
          numUnits: listing.numUnits,
          propertyId: listingAliasFromQuery || listing.listingAlias || '',
          propertyType: listing.propertyType as (typeof PROPERTY_TYPES)[keyof typeof PROPERTY_TYPES],
        },
        {
          onSuccess: () => {
            window.location.reload();
            queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
          },
          onError: (err) => {
            if (err.message) {
              setErrorObj({ type: 'address', payload: err.message });
              handleErrorReset();
            }
          },
        },
      );
    } catch (error) {
      // Error already handled in onError callback
      logError({ error: 'Failed to edit property:', errorType: 'EditProperty' });
    }
  };

  const handleMultiplexUnityEntryCreateStart = () => {
    setIsMultiplexProcessing(true);
    setIsPrimaryButtonLoading(true);
    setIsSubmitDisabled(true);
  };

  const handleMultiplexUnityEntrySuccess = ({
    multiplexBuildingSessionId,
    unitsCreated,
    hasPartialUnitsSucceeded,
  }: {
    multiplexBuildingSessionId: string;
    unitsCreated: Array<UserPropertyMultiplexCreateUnitResponseWebDto>;
    hasPartialUnitsSucceeded: boolean;
  }) => {
    setUnitsCreated(unitsCreated);
    setHasPartialUnitsSucceeded(hasPartialUnitsSucceeded);

    if (user) {
      const unitsCreatedList = unitsCreated.map((unit) => unit.listingAlias).join(',');

      const csEventParams = {
        currentProperty: {
          listingAlias: unitsCreatedList,
        },
        user: user ?? {},
      };

      emitMultiplexConfirmUnitEntryClickEvent(csEventParams);
    }

    if (canAccessMultiplexListingFlow) {
      // skip MultiplexUnitsAdded step
      const firstUnitListingAlias = unitsCreated[0].listingAlias;

      if (firstUnitListingAlias) {
        setIsNewListing(true);

        const query = new URLSearchParams({ multiplexBuildingSessionId });
        // TODO: after this page is fully migrated, use v10 router.push with getListingRoute
        window.location.href = `/rental-manager/properties/${firstUnitListingAlias}/create-listing?${query.toString()}`;
      }
      return;
    }

    setIsMultiplexProcessing(false);
    setStep(STEPS.UNITS_ADDED);

    setIsPrimaryButtonLoading(false);
    // disable submit until a unit is selected
    setIsSubmitDisabled(true);
  };

  const handleMultiplexUnityEntryErrorChange = ({
    hasError,
    isPending,
  }: {
    hasError?: boolean | undefined;
    isPending?: boolean | undefined;
  }) => {
    if (isPending) {
      setIsMultiplexProcessing(true);
      setIsPrimaryButtonLoading(true);
      setIsSubmitDisabled(true);
      return;
    }

    if (hasError) {
      setIsMultiplexProcessing(false);
      setIsPrimaryButtonLoading(false);
      setIsSubmitDisabled(true);
      return;
    }

    if (isMultiplexProcessing) {
      // Mutation finished; stay locked until redirect (isPending clears before navigation completes).
      setIsPrimaryButtonLoading(true);
      setIsSubmitDisabled(true);
      return;
    }

    setIsPrimaryButtonLoading(false);
    setIsSubmitDisabled(false);
  };

  const handleActiveUnitIndexChange = (activeUnitIndex: number | null) => {
    setIsSubmitDisabled(activeUnitIndex === null);
  };

  const handlePrimaryButtonClick = async () => {
    switch (step) {
      case STEPS.INPUT_ADDRESS: {
        const isCommunityType = listing?.propertyType === COMMUNITY;

        const isRoomForRentCheckboxDisplayed = shouldDisplayRoomForRentCheckbox(
          isMultiplexType,
          isCommunityType,
          behavior,
        );

        setIsPrimaryButtonLoading(true);
        setIsSelectActionFinalStep(false);
        verifyInputAndGoToConfirmSteps(
          () => emitCreateListingEvent({ user }),
          // onValidationSuccess
          () => {
            // set isRoomForRent to false if checkbox was not displayed
            if (isRoomForRentCheckboxDisplayed === false) {
              setListing({
                type: 'isRoomForRent',
                payload: false,
              });
            }
          },
        );

        if (isValidatedWithoutUnitDetected) {
          setIsValidatedWithoutUnitDetected(false);
        }

        if (unitNumberDetected && listing.unit) {
          setUnitNumberDetected(false);
        }

        break;
      }
      case STEPS.CONFIRM_ADDRESS: {
        setIsPrimaryButtonLoading(true);
        setIsSubmitDisabled(true);
        if (behavior === BEHAVIOR_OPTIONS.ADD) {
          try {
            if (isMultiplexType && user) {
              emitMultiplexConfirmAddressClickEvent({ user });
              // don't create the listing, go to unit entry step
              setStep(STEPS.UNIT_ENTRY);
              setIsPrimaryButtonLoading(false);
              setIsSubmitDisabled(false);
              return;
            }

            const { path, additionalData } = await createProperty();
            if (isSelectActionFinalStep) {
              setStep(STEPS.SELECT_ACTION);
            } else if (path) {
              if (shouldRedirectToPayments) {
                window.location.href = `${EXTERNAL_ROUTES.PAYMENTS_DASHBOARD}/${additionalData?.listingAlias}`;
              } else {
                // TODO: after this page is fully migrated, use v10 router.push
                window.location.href = path;
              }
              onClose();
            }
          } catch (err) {
            const isMultiFamilyRestricted = err.code === IS_MULTI_FAMILY_RESTRICTED;
            const fieldWithError = isMultiFamilyRestricted ? 'propertyType' : 'address';

            setErrorObj({ type: fieldWithError, payload: err.message });
            handleErrorReset();
            return { path: null };
          }
        } else {
          editProperty();
        }

        break;
      }
      case STEPS.UNIT_ENTRY: {
        multiplexUnitEntryRef.current?.createMultiplex?.();
        break;
      }
      case STEPS.UNITS_ADDED: {
        const activeUnitListingAlias = multiplexUnitsAddedRef.current?.getActiveUnitListingAlias?.();

        const currentPropertyWithSelectedListingAlias: ZrmClickstreamListing = {
          ...listing,
          listingAlias: activeUnitListingAlias ?? undefined,
        };

        if (user) {
          emitMultiplexAddListingDetailsClickEvent({
            currentProperty: currentPropertyWithSelectedListingAlias,
            user,
          });
        }
        multiplexUnitsAddedRef.current?.goToSelectedUnitListingCreation?.();
        break;
      }
      default: {
        throw Error('unrecognized step: ' + step);
      }
    }
  };

  const handleSecondaryButtonClick = () => {
    switch (step) {
      case STEPS.INPUT_ADDRESS:
        setIsSecondaryButtonLoading(true);
        setIsSelectActionFinalStep(true);
        verifyInputAndGoToConfirmSteps(() => emitSeeAllActionsEvent({ user }));
        break;
      case STEPS.CONFIRM_ADDRESS:
        emitGoBackAndEditAddressEvent({ user });
        setStep(STEPS.INPUT_ADDRESS);
        setIsRefocusingAddressInput(true);
        break;
      default:
        throw Error('unrecognized step: ' + step);
    }
  };

  const primaryButtonDataEventId = useMemo(() => {
    switch (step) {
      case STEPS.INPUT_ADDRESS:
        return ADD_PROPERTY_EVENT_IDS.CREATE_LISTING;

      case STEPS.UNIT_ENTRY:
        return MULTIPLEX_EVENT_IDS.MULTIPLEX_CONFIRM_UNIT_ENTRY_CLICK;

      case STEPS.UNITS_ADDED:
        return MULTIPLEX_EVENT_IDS.MULTIPLEX_ADD_LISTING_DETAILS_CLICK;

      default:
        if (isSelectActionFinalStep) {
          return ADD_PROPERTY_EVENT_IDS.CONFIRM_SEE_ALL_ACTIONS;
        }

        return ADD_PROPERTY_EVENT_IDS.CONFIRM_CREATE_LISTING;
    }
  }, [step, isSelectActionFinalStep]);

  const getPrimaryButtonTitle = () => {
    if (behavior === BEHAVIOR_OPTIONS.EDIT) {
      return 'Save changes';
    } else {
      const nonMultiplexButtonTitle = shouldRedirectToPayments ? 'Start collecting rent' : 'Confirm';

      const confirmAddressButtonTitle = isMultiplexType ? 'Yes, continue' : nonMultiplexButtonTitle;

      switch (step) {
        case STEPS.INPUT_ADDRESS:
          return 'Create listing';
        case STEPS.CONFIRM_ADDRESS:
          return confirmAddressButtonTitle;
        case STEPS.UNIT_ENTRY:
          return 'Save and continue';
        case STEPS.UNITS_ADDED:
          return 'Add listing details';
        default:
          return '';
      }
    }
  };

  const getSecondaryButtonTitle = () => {
    switch (step) {
      case STEPS.INPUT_ADDRESS:
        return 'See all available actions';
      case STEPS.CONFIRM_ADDRESS:
        return 'Edit property information';
      default:
        return '';
    }
  };

  const getSubheadingText = () => {
    if (shouldRedirectToPayments) {
      return 'Once you add your property, you can start collecting rent payments for free.';
    } else {
      return 'Once you add your property, you can list it for free on Zillow, Trulia, and HotPads to help find your perfect renter.';
    }
  };

  return (
    <Modal
      scrollDocument={false}
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'lg' }}
      data-eventid={ADD_PROPERTY_EVENT_IDS.OPEN}
    >
      <Modal.Header>
        <Heading level={1} textStyle="heading-xs">
          Add property
        </Heading>
      </Modal.Header>
      <Divider />
      <Modal.Body>
        <div aria-live="polite">
          {step === STEPS.INPUT_ADDRESS && (
            <Box
              css={{
                paddingBlock: { lg: '800' },
                paddingLeft: { lg: '800' },
                paddingRight: { lg: '20px' },
              }}
            >
              <Icon
                size="xl"
                css={{ color: 'icon.subtle', marginTop: { base: '600', md: 0 } }}
                render={<IconHouseFilled />}
              />
              <Heading level={2} textStyle="heading-sm" css={{ marginBlock: { base: '400', md: '600' } }}>
                First, let&rsquo;s add your property
              </Heading>

              <Paragraph
                css={{
                  paddingBottom: { mdDown: '200' },
                  marginBottom: {
                    md:
                      unitNumberDetected ||
                      isValidatedWithoutUnitDetected ||
                      Boolean(propertyAlreadyPostedWarningMessage)
                        ? '600'
                        : '800',
                  },
                }}
              >
                {getSubheadingText()}
              </Paragraph>

              <AddPropertyForm
                listing={listing}
                contracts={contracts}
                errorObj={errorObj}
                isSubmitDisabled={isSubmitDisabled}
                setIsSubmitDisabled={setIsSubmitDisabled}
                behavior={behavior}
                unitNumberDetected={unitNumberDetected}
                isValidatedWithoutUnitDetected={isValidatedWithoutUnitDetected}
                propertyAlreadyPostedWarningMessage={propertyAlreadyPostedWarningMessage}
                onListingChange={handleListingChange}
                onCheckLocationRestriction={(keyName, newValue) =>
                  handleCheckLocationRestriction(onChangePropertyType, keyName, newValue)
                }
                isRefocusingAddressInput={isRefocusingAddressInput}
                setIsRefocusingAddressInput={setIsRefocusingAddressInput}
              />
            </Box>
          )}
          {step === STEPS.CONFIRM_ADDRESS && (
            <Box css={{ padding: { lg: '800' } }} data-testid="AddPropertyModal-ConfirmAddress">
              <Icon
                size="xl"
                css={{
                  color: 'icon.subtle',
                  marginTop: { base: '600', md: 0 },
                  marginBottom: '600',
                }}
                render={<IconHouseFilled />}
              />
              <Heading level={2} textStyle="heading-sm" css={{ paddingBottom: { base: '600', lg: '800' } }}>
                Is this information complete and correct?
              </Heading>

              <ConfirmAddress
                address={listing.address || ''}
                isRoomForRent={listing.isRoomForRent ?? false}
                numUnits={listing.numUnits}
                propertyType={listing.propertyType === PROPERTY_TYPES.CONDO ? 'Condo' : listing.propertyType || ''}
                unit={listing.unit}
                showImpreciseLocationAlert={isImpreciseLocation}
                user={user ?? undefined}
              />
            </Box>
          )}
          {step === STEPS.SELECT_ACTION && listing.listingAlias && (
            <SelectAvailableActions
              listingAlias={listing.listingAlias}
              triggerObject="posting_path"
              user={user ?? undefined}
            />
          )}
          {user?.features?.canDeleteListing &&
            user?.isPostingPathAdmin &&
            !propertyDetailsData?.isFeed &&
            listing.listingAlias &&
            behavior === BEHAVIOR_OPTIONS.EDIT && (
              <Box css={{ paddingInline: { base: '0', lg: '800' } }}>
                <DeleteButton listingAlias={listing.listingAlias} />
              </Box>
            )}
          {step === STEPS.UNIT_ENTRY && listing.address && (
            <MultiplexUnitEntry
              ref={multiplexUnitEntryRef}
              customerId={listing.customerId}
              fullAddress={listing.address}
              numUnits={listing.numUnits}
              onSuccess={handleMultiplexUnityEntrySuccess}
              onErrorChange={handleMultiplexUnityEntryErrorChange}
              onCreateStart={handleMultiplexUnityEntryCreateStart}
            />
          )}
          {step === STEPS.UNITS_ADDED && (
            <MultiplexUnitsAdded
              ref={multiplexUnitsAddedRef}
              hasPartialUnitsSucceeded={hasPartialUnitsSucceeded}
              unitsCreated={unitsCreated}
              onActiveUnitIndexChange={handleActiveUnitIndexChange}
            />
          )}
        </div>
      </Modal.Body>
      <Divider />
      {step !== STEPS.SELECT_ACTION && (
        <Modal.Footer css={{ justifyContent: step === STEPS.CONFIRM_ADDRESS ? 'space-between' : 'flex-end' }}>
          {step === STEPS.CONFIRM_ADDRESS && (
            <Box css={{ width: '40%' }}>
              <ListingActivationTerms actionText="By continuing" />
            </Box>
          )}

          <ButtonGroup aria-label="Action Buttons">
            {!shouldRedirectToPayments && !(step === STEPS.INPUT_ADDRESS && isMultiplexType) && (
              <TextButton
                tone="brand"
                onClick={handleSecondaryButtonClick}
                disabled={isSubmitDisabled}
                data-eventid={
                  step === STEPS.INPUT_ADDRESS
                    ? ADD_PROPERTY_EVENT_IDS.SEE_ALL_ACTIONS
                    : ADD_PROPERTY_EVENT_IDS.GO_BACK_AND_EDIT_ADDRESS
                }
              >
                {isSecondaryButtonLoading ? (
                  <Spinner size="sm" css={{ marginRight: '10px' }} />
                ) : (
                  getSecondaryButtonTitle()
                )}
              </TextButton>
            )}
            <Button
              data-testid="primary-button"
              data-eventid={primaryButtonDataEventId}
              fluid={false}
              tone="brand"
              emphasis="filled"
              onClick={handlePrimaryButtonClick}
              disabled={isSubmitDisabled}
              loading={isPrimaryButtonLoading}
            >
              {getPrimaryButtonTitle()}
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default AddPropertyModal;
